import ModuleContext from 'CMFW/context/ModuleContext';
import Grid from 'CMFW/grid/Grid';
import {
  ContainerLeft,
  ContainerLeftHeader,
  ContainerRight,
  EditorRow,
  ModuleContainer,
} from 'components/common/styles';
import { Split } from 'components/Split';
import { useForceRender } from 'hooks/useForceRender';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { IFields } from '../../../CMFW/grid/fields';
import MemoryPatternConsistencyConfig, {
  IPatternConsistencyConfig,
} from '../components/MemoryPatternConsistencyConfig';
import MemoryPatternConsistencyModel from '../../../models/MemoryPatternConsistencyModel';
import MemoryPatternConsistencyItemsModel from '../../../models/MemoryPatternConsistencyItemsModel';
import { Button, TextField, IconButton } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

let pcFilterText = '';

const MemoryPatternConsistency: React.FC = () => {
  const forceRender = useForceRender();
  const [showResults, setShowResults] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');

  const groupsModel = useMemo(() => new MemoryPatternConsistencyModel({ render: forceRender }), [forceRender]);
  const itemsModel = useMemo(() => new MemoryPatternConsistencyItemsModel({ render: forceRender }), [forceRender]);

  const cellRenderers = useMemo(
    () => ({
      [IFields.moduleNavigation]: 'multiNavigationRenderer',
      [IFields.actions]: 'msuChangeRenderer',
    }),
    [],
  );

  const handleGenerate = async (config: IPatternConsistencyConfig) => {
    setIsProcessing(true);
    try {
      setShowResults(true);
      if (typeof config.dialect === 'number') {
        groupsModel.setDialectId(config.dialect);
        itemsModel.setDialectId(config.dialect);
      }
      itemsModel.setParentModel(groupsModel);
      await groupsModel.loadWithConfig(config);
      itemsModel.reset();
    } catch (e) {
      console.error('Error generating Pattern Consistency:', e);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackToConfig = () => setShowResults(false);

  // Debounce filter input
  React.useEffect(() => {
    const h = setTimeout(() => setFilterText(filterInput), 300);
    return () => clearTimeout(h);
  }, [filterInput]);

  // Update module-level filter text so Grid callbacks see the latest
  pcFilterText = filterText || '';

  // External filter helpers for left grid (same pattern as Semantic Unifier)
  const pcIsExternalFilterActive = () => (pcFilterText || '').trim() !== '';
  const pcDoesExternalFilterPass = (node: any) => {
    const t = (pcFilterText || '').trim();
    if (t === '') return true;
    let re: RegExp | null = null;
    try {
      re = new RegExp(t, 'i');
    } catch {
      re = null;
    }
    const data: any = node?.data || {};
    const rep = String(data.us_pattern || '');
    const members: any[] = Array.isArray(data.members) ? data.members : [];
    const matchStr = (s: string) => (re ? re.test(s) : s.toLowerCase().includes(t.toLowerCase()));
    if (matchStr(rep)) return true;
    for (const m of members) {
      const mp = String(m?.pattern || '');
      if (matchStr(mp)) return true;
    }
    return false;
  };

  return (
    <ModuleContainer>
      <ConfigContainer style={{ display: showResults ? 'none' : 'block' }}>
        <ContainerLeft>
          <MemoryPatternConsistencyConfig onGenerate={handleGenerate} />
        </ContainerLeft>
      </ConfigContainer>

      <ResultsContainer style={{ display: showResults ? 'flex' : 'none' }}>
        <Split widthLeft={600}>
          <ContainerLeft>
            <ContainerLeftHeader>
              <EditorRowStart>
                <Button variant="outlined" size="small" onClick={handleBackToConfig} disabled={isProcessing}>
                  Back to Configuration
                </Button>
                <ResultsInfo>
                  {Array.isArray(groupsModel.gridData) ? groupsModel.gridData.length : 0} groups
                </ResultsInfo>
                <FilterRow>
                  <TextFieldStyled
                    variant="outlined"
                    size="small"
                    placeholder="Filter groups"
                    value={filterInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterInput(e.target.value)}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      setFilterInput('');
                      setFilterText('');
                    }}
                    disabled={!filterInput}
                    title="Clear"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </IconButton>
                </FilterRow>
              </EditorRowStart>
            </ContainerLeftHeader>
            <ModuleContext.Provider value={groupsModel}>
              <Grid
                isFilterActive={() => pcIsExternalFilterActive()}
                isRowVisible={pcDoesExternalFilterPass}
                handleRowClick={(data: any) => {
                  const gid = data?.id;
                  const fresh = Array.isArray(groupsModel.gridData)
                    ? (groupsModel.gridData as any).find((g: any) => g && g.id === gid)
                    : null;
                  const groupObj = fresh || data;
                  const members = Array.isArray(groupObj?.members) ? groupObj.members : [];
                  itemsModel.setCurrentGroup(groupObj);
                  itemsModel.setMembers(members);
                }}
              />
            </ModuleContext.Provider>
          </ContainerLeft>
          <ContainerRight>
            <ModuleContext.Provider value={itemsModel}>
              <Grid cellRenderers={cellRenderers} disableRowLoadFields={[IFields.actions]} />
            </ModuleContext.Provider>
          </ContainerRight>
        </Split>
      </ResultsContainer>
    </ModuleContainer>
  );
};

export default MemoryPatternConsistency;

const EditorRowStart = styled(EditorRow)`
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
`;

const ConfigContainer = styled.div`
  flex: 1;
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const ResultsContainer = styled.div`
  flex: 1;
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const ResultsInfo = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const TextFieldStyled = styled(TextField)`
  flex: 1;
`;
